/**
 * Event logging utility for pushing user events to the data layer
 */

import { logError } from '@utils/tools/ErrorLoggers';

/**
 * Common optional metadata fields for event tracking
 * These fields provide additional context for analytics without being required
 */
export interface IEventMetadata {
    elementId?: string;
    dataAutomationId?: string;
    pageCategory?: string;
    content?: string;
    componentName?: string;
}

export interface IEventData extends IEventMetadata {
    /** Event name/type (required) */
    event: string;
    eventCategory: string;
    eventAction: string;
    eventLabel?: string | number | boolean;
    [key: string]: string | number | boolean | undefined;
}

declare global {
    interface Window {
        dataLayer?: Array<IEventData>;
    }
}

/**
 * Ensures dataLayer is initialized (lazy initialization)
 * @returns true if dataLayer is available, false otherwise
 */
const ensureDataLayer = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    
    try {
        if (!window.dataLayer) {
            window.dataLayer = [];
        }
        return true;
    } catch (error) {
        // Handle cases where window.dataLayer might be read-only or other errors
        return false;
    }
};

/**
 * Adds a single event to the data layer
 * This is a fire-and-forget operation - failures are logged but not returned
 * @param eventInput - JSON object containing event, eventCategory, eventAction, eventLabel, and any additional properties
 */
const addEvent = (eventInput: IEventData): void => {
    // Validate required fields are non-empty strings
    const requiredFields = {
        event: eventInput.event,
        eventCategory: eventInput.eventCategory,
        eventAction: eventInput.eventAction,
    };

    const missingFields: string[] = [];
    const invalidFields: string[] = [];

    for (const [fieldName, fieldValue] of Object.entries(requiredFields)) {
        if (fieldValue === undefined || fieldValue === null) {
            missingFields.push(fieldName);
        } else if (typeof fieldValue !== 'string' || fieldValue.trim().length === 0) {
            invalidFields.push(fieldName);
        }
    }

    if (missingFields.length > 0 || invalidFields.length > 0) {
        // Log validation errors using safe logger (PII-safe, dev-only)
        logError('addEvent: Invalid event data', {
            handler: 'addEvent',
            missingFields: missingFields.length > 0 ? missingFields.join(', ') : undefined,
            invalidFields: invalidFields.length > 0 ? invalidFields.join(', ') : undefined,
        });
        return;
    }

    // Ensure dataLayer exists before building event data
    if (!ensureDataLayer()) {
        // Only log if window exists (initialization failed)
        if (typeof window !== 'undefined') {
            logError('addEvent: Failed to initialize dataLayer. Event dropped.', {
                handler: 'addEvent',
            });
        }
        return;
    }

    // Process eventLabel - handle string, number, and boolean types
    let processedEventLabel: string | number | boolean | undefined = undefined;
    if (eventInput.eventLabel !== undefined && eventInput.eventLabel !== null) {
        if (typeof eventInput.eventLabel === 'string') {
            const trimmed = eventInput.eventLabel.trim();
            if (trimmed.length > 0) {
                processedEventLabel = trimmed;
            }
        } else if (typeof eventInput.eventLabel === 'number' && !isNaN(eventInput.eventLabel) && isFinite(eventInput.eventLabel)) {
            // Validate number is not NaN and is finite
            processedEventLabel = eventInput.eventLabel;
        } else if (typeof eventInput.eventLabel === 'boolean') {
            // Booleans are always valid
            processedEventLabel = eventInput.eventLabel;
        }
    }

    const eventData: IEventData = {
        event: eventInput.event,
        eventCategory: eventInput.eventCategory,
        eventAction: eventInput.eventAction,
        ...(processedEventLabel !== undefined && { eventLabel: processedEventLabel }),
        ...Object.fromEntries(
            Object.entries(eventInput).filter(([key, value]) => 
                !['event', 'eventCategory', 'eventAction', 'eventLabel'].includes(key) &&
                value !== undefined && value !== null
            )
        )
    };

    // Push to dataLayer (ensureDataLayer guarantees it exists at this point)
    try {
        window.dataLayer!.push(eventData);
    } catch (error) {
        // Log error using safe logger (PII-safe, dev-only)
        logError('addEvent: Failed to push event to dataLayer', {
            handler: 'addEvent',
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};

export { addEvent };

